/**
 * SectionEquations — Claude / skyworks.ai style equation cards.
 *
 * Reads structured equation entries attached to a section via the
 * EquationStudio "Insert into section" flow. Each entry has the shape:
 *   {
 *     latex:    "E = mc^2",          // required — KaTeX-renderable
 *     title:    "Mass-energy",        // optional headline
 *     plain:    "Energy equals mass…", // optional one-liner explanation
 *     subject:  "physics" | "math" | "chemistry" | "biology" | …,
 *     mode:     "block" | "inline",
 *     variables:[{sym:"E", name:"…", unit:"J"}, …],
 *     steps:    [{order, description, rule?, latex}, …],
 *   }
 *
 * Visual goals (matching the Claude / skyworks.ai bar)
 *   • Subject-tinted left rail + soft pastel header (Math / Physics / Chem / Bio)
 *   • Auto-numbered equations (1), (2), … rendered as a right-aligned label
 *   • One-tap "Copy LaTeX" button per equation
 *   • Collapsible "Show derivation" footer when steps are present
 *   • Variables formatted as a neat 2-col grid with units in fading grey
 *   • Works inside both editor preview and the student LessonView dark theme
 */
import React, { useState } from 'react';
import { InlineMath, BlockMath } from 'react-katex';
import 'katex/dist/katex.min.css';
import 'katex/dist/contrib/mhchem.js';
import {
  Sigma, BookOpen, Atom, Calculator, Beaker, X, ListOrdered,
  Copy, Check, ChevronDown, ChevronUp,
} from 'lucide-react';

const SUBJECT_THEMES = {
  math:      { accent: '#10B981', soft: '#ECFDF5', tint: 'rgba(16,185,129,0.07)',  icon: Calculator, label: 'Math'      },
  physics:   { accent: '#0EA5E9', soft: '#E0F2FE', tint: 'rgba(14,165,233,0.07)',  icon: Atom,       label: 'Physics'   },
  chemistry: { accent: '#A855F7', soft: '#F5F0FF', tint: 'rgba(168,85,247,0.07)',  icon: Beaker,     label: 'Chemistry' },
  biology:   { accent: '#16A34A', soft: '#F0FDF4', tint: 'rgba(22,163,74,0.07)',   icon: BookOpen,   label: 'Biology'   },
  default:   { accent: '#0d9488', soft: '#F0FDFA', tint: 'rgba(13,148,136,0.07)',  icon: Sigma,      label: 'Equation'  },
};

function themeFor(subject) {
  const k = (subject || 'default').toLowerCase();
  return SUBJECT_THEMES[k] || SUBJECT_THEMES.default;
}

function safe(latex) {
  if (!latex) return '';
  let s = String(latex).trim();
  if (s.startsWith('$$') && s.endsWith('$$')) s = s.slice(2, -2);
  else if (s.startsWith('$') && s.endsWith('$')) s = s.slice(1, -1);
  return s;
}

function MathBlock({ latex, mode }) {
  const s = safe(latex);
  if (!s) return null;
  try {
    return mode === 'inline'
      ? <InlineMath math={s} />
      : <BlockMath math={s} />;
  } catch {
    return <code className="text-rose-600 text-xs">[math error] {s}</code>;
  }
}

function CopyButton({ value, label = 'Copy LaTeX', testid }) {
  const [done, setDone] = useState(false);
  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        // Catch the Promise rejection — clipboard API can be denied in
        // headless test envs or cross-origin iframes. The visual "Copied"
        // flag is best-effort UX, not a guaranteed contract.
        try {
          const p = navigator.clipboard?.writeText(value || '');
          if (p && typeof p.catch === 'function') p.catch(() => {});
          setDone(true);
          setTimeout(() => setDone(false), 1500);
        } catch { /* clipboard API missing → silent no-op */ }
      }}
      className="inline-flex items-center gap-1.5 text-xs font-semibold px-2 py-1 rounded-md border bg-white/70 hover:bg-white text-slate-500 hover:text-slate-900 transition-colors"
      style={{ borderColor: 'rgba(100,116,139,0.18)' }}
      title={label}
      data-testid={testid}>
      {done ? <Check className="w-3 h-3 text-green-500" /> : <Copy className="w-3 h-3" />}
      {done ? 'Copied' : label}
    </button>
  );
}

/**
 * @param {Object[]} equations
 * @param {Function} [onDelete] — (idx) => void; if present shows delete X
 * @param {boolean}  [compact]  — reduces padding for in-editor view
 * @param {string}   [numbering] — prefix for equation numbers (e.g. "3.")
 */
export default function SectionEquations({ equations = [], onDelete = null, compact = false, numbering = '' }) {
  const [openSteps, setOpenSteps] = useState(() => new Set());

  if (!equations || equations.length === 0) return null;

  const toggle = (idx) => setOpenSteps((s) => {
    const next = new Set(s);
    if (next.has(idx)) next.delete(idx); else next.add(idx);
    return next;
  });

  return (
    <div className="space-y-4" data-testid="section-equations">
      {equations.map((eq, idx) => {
        const T = themeFor(eq.subject);
        const Icon = T.icon;
        const eqNumber = numbering ? `${numbering}${idx + 1}` : `${idx + 1}`;
        const hasSteps = Array.isArray(eq.steps) && eq.steps.length > 0;
        const isOpen = openSteps.has(idx);

        return (
          <div
            key={idx}
            className={`relative rounded-2xl border overflow-hidden group ${compact ? 'p-3' : 'p-5'}`}
            style={{
              borderColor: `${T.accent}26`,
              background: `linear-gradient(180deg, ${T.tint}, transparent 40%), #ffffff`,
            }}
            data-testid={`equation-block-${idx}`}>
            {/* Subject rail */}
            <div className="absolute top-0 left-0 w-[3px] h-full" style={{ background: T.accent }} />

            {/* Header row — subject pill + equation number + copy + delete */}
            <div className="flex items-start justify-between gap-3 pl-3">
              <div className="flex items-center gap-2 min-w-0 flex-1">
                <div className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0"
                     style={{ background: T.soft, color: T.accent }}>
                  <Icon className="w-4 h-4" />
                </div>
                <div className="min-w-0">
                  <div className="text-xs font-semibold " style={{ color: T.accent }}>
                    {T.label}
                  </div>
                  {eq.title && (
                    <div className="text-sm font-semibold text-slate-900 truncate">{eq.title}</div>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <CopyButton value={safe(eq.latex)} label="LaTeX" testid={`equation-copy-${idx}`} />
                <span
                  className="text-xs font-mono font-semibold px-2 py-1 rounded-md tabular-nums"
                  style={{ background: T.soft, color: T.accent }}
                  data-testid={`equation-number-${idx}`}>
                  ({eqNumber})
                </span>
                {onDelete && (
                  <button
                    onClick={() => onDelete(idx)}
                    data-testid={`equation-delete-${idx}`}
                    className="opacity-0 group-hover:opacity-100 transition text-slate-400 hover:text-rose-500 p-1 rounded hover:bg-rose-50"
                    title="Remove equation">
                    <X className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            </div>

            {/* Math body — scrollable on overflow so long lines don't break the layout */}
            <div className="pl-3 mt-4">
              <div
                className="rounded-xl border bg-white/80 px-4 py-5 flex items-center justify-center overflow-x-auto"
                style={{ borderColor: `${T.accent}18` }}
                data-testid={`equation-math-${idx}`}>
                <MathBlock latex={eq.latex} mode={eq.mode} />
              </div>
            </div>

            {/* Plain-language explanation */}
            {eq.plain && (
              <p className="pl-3 mt-3 text-xs text-slate-600 italic leading-relaxed">
                {eq.plain}
              </p>
            )}

            {/* Variables */}
            {Array.isArray(eq.variables) && eq.variables.length > 0 && (
              <div className="pl-3 mt-4">
                <div className="text-xs font-semibold text-slate-500 mb-2">Variables</div>
                <div
                  className="grid sm:grid-cols-2 gap-x-4 gap-y-2 text-xs text-slate-700 px-3 py-2 rounded-lg"
                  style={{ background: T.tint }}>
                  {eq.variables.map((v, i) => (
                    <div key={i} className="leading-relaxed flex items-baseline gap-2">
                      <span className="font-mono font-semibold text-slate-900 min-w-[18px]" style={{ color: T.accent }}>
                        {v.sym || v.symbol}
                      </span>
                      <span className="flex-1 min-w-0">
                        {(v.name || v.meaning) && <span className="text-slate-700">{v.name || v.meaning}</span>}
                        {v.unit && <span className="text-slate-400"> · {v.unit}</span>}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Steps — collapsible derivation */}
            {hasSteps && (
              <div className="pl-3 mt-4">
                <button
                  type="button"
                  onClick={() => toggle(idx)}
                  className="w-full flex items-center justify-between text-xs font-semibold text-slate-500 hover:text-slate-700 py-2"
                  data-testid={`equation-toggle-steps-${idx}`}>
                  <span className="inline-flex items-center gap-1.5">
                    <ListOrdered className="w-3 h-3" /> Step-by-step ({eq.steps.length})
                  </span>
                  {isOpen ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                </button>
                {isOpen && (
                  <ol className="space-y-2.5 mt-2" data-testid={`equation-steps-${idx}`}>
                    {eq.steps.map((step, i) => (
                      <li key={i} className="rounded-lg border bg-white/70 p-3" style={{ borderColor: `${T.accent}14` }}>
                        <div className="flex items-baseline gap-2">
                          <span
                            className="text-xs font-mono font-semibold px-1.5 py-0.5 rounded flex-shrink-0"
                            style={{ background: T.soft, color: T.accent }}>
                            {step.order || (i + 1)}
                          </span>
                          <div className="flex-1 min-w-0">
                            <div className="text-xs font-semibold text-slate-800">{step.description}</div>
                            {step.rule && (
                              <div className="text-xs italic text-slate-500 mt-0.5">{step.rule}</div>
                            )}
                          </div>
                        </div>
                        {step.latex && (
                          <div className="mt-2 bg-white rounded-md px-3 py-2 inline-block border" style={{ borderColor: `${T.accent}10` }}>
                            <MathBlock latex={step.latex} />
                          </div>
                        )}
                      </li>
                    ))}
                  </ol>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
